import templateUrl from './detectorWizardSettings.tpl.html';
import { safeLookup } from '@splunk/olly-utilities/lib/sfUtilities/sfUtilities';

const DEFAULT_RESOLUTION_PERIOD = 30 * 60 * 1000;
const DEFAULT_TIMEOUT = 10 * 60 * 1000;

const autoClearMetadata = {
    displayName: 'Auto-Clear alerts',
    description: 'Clear active alerts after a specified time has elapsed',
    infoUrl:
        'https://quickdraw.splunk.com/redirect/?product=Observability&location=zombie.alert&version=current',
};

/**
 * The alert settings component allows user to edit rule condition
 * for selected alert condition.
 * All the binding functions and callbacks refer to RuleConditionController
 * so we reuse existing detector functions rule condition modal code.
 */
angular.module('signalview.detector.wizard').component('detectorWizardAlertSettings', {
    bindings: {
        rule: '<',
        readable: '<',
        plots: '<',
        numPlots: '<',
        category: '<',
        currentSelection: '<',
        jobInfo: '<',
        resolvingResolution: '<',
        signalResolution: '<',
        functionInputError: '<',
        isValid: '<',
        compoundConditionsEnabled: '<',
        preSelectedSignal: '<',
        functionSelector: '<',
        functionTrackBy: '<',
        sensitivityModel: '<',
        sharedPreflightState: '<',
        isNewRule: '<',
        updateRule: '&',
        updateSensitivity: '&',
        updateFuncInputValue: '&',
        removeFuncInputValue: '&',
        updateFunctionSelector: '&',
        proceed: '&',
        done: '&',
        onSelectTab: '&',
        toggleExpand: '&',
        expand: '<',
        orgSettings: '<',
    },
    templateUrl,
    controller: [
        '$scope',
        '_',
        'signalStream',
        'chartbuilderUtil',
        'plotUtils',
        'valueFormatter',
        '$timeout',
        'programTextUtils',
        'featureEnabled',
        function (
            $scope,
            _,
            signalStream,
            chartbuilderUtil,
            plotUtils,
            valueFormatter,
            $timeout,
            programTextUtils,
            featureEnabled
        ) {
            const ctrl = this;
            const orOperator = 'OR';
            const andOperator = 'AND';
            const defaultOperator = orOperator;

            ctrl.$onInit = $onInit;
            ctrl.addCompoundCondition = addCompoundCondition;
            ctrl.removeCondition = removeCondition;
            ctrl.moveCondition = moveCondition;
            ctrl.autoClearMetadata = autoClearMetadata;
            ctrl.suggestedThresholdState = { status: 'loading' };
            ctrl.showSuggestedThreshold = false;
            ctrl.isSuggestedThresholdEnabled = false;

            const initCallbacks = [];
            let suggestionStreamHandle = null;
            let suggestionTimeoutPromise = null;
            let latestHigherValue = null;
            let latestLowerValue = null;
            let suggestionTsidToMetadata = {};
            const plotRollupByKey = {};

            ctrl.compoundOrderTitles = [
                'First',
                'Second',
                'Third',
                'Fourth',
                'Fifth',
                'Sixth',
                'Seventh',
                'Eighth',
                'Ninth',
                'Tenth',
            ];

            function $onInit() {
                ctrl.isSuggestedThresholdEnabled = featureEnabled('detectorSuggestThreshold');
                $scope.sectionTabs = [
                    {
                        id: 'settings',
                        name: 'Alert Settings',
                    },
                    {
                        id: 'event',
                        name: 'Simulated Events',
                    },
                    {
                        id: 'data',
                        name: 'Data Table',
                    },
                ];
                $scope.tabIndices = {};
                $scope.sectionTabs.forEach(function (tab, index) {
                    $scope.tabIndices[tab.id] = index;
                });

                $scope.showAdvanced = false;
                $scope.selectTab($scope.sectionTabs[$scope.tabIndices.settings]); // settings
                $scope.legacyJob = {};
                // when local variable changes, update with parent binding
                if (initCallbacks.length) {
                    initCallbacks.forEach((s) => s());
                }
                initCallbacks.push(
                    $scope.$on('detector wizard selected stage', function (ev, stage) {
                        onUpdateTabStage('', stage);
                    })
                );
                initCallbacks.push(
                    $scope.$on('go to tab', function (ev, tabId, stage) {
                        onUpdateTabStage(tabId, stage);
                    })
                );
                initCallbacks.push($scope.$on('plotTimeSeriesData', onPlotTimeSeriesData));
                initCallbacks.push(
                    $scope.$on('$destroy', function () {
                        cleanupSuggestionStream();
                    })
                );
            }

            function checkForMoreOptions() {
                $scope.hasAdvancedOptions = false;
                if (ctrl.currentSelection) {
                    angular.forEach(ctrl.currentSelection.inputs, function (val) {
                        if (val.aboveTheFold === false) {
                            $scope.hasAdvancedOptions = true;
                        }
                    });
                }
            }

            function onUpdateTabStage(tabId, stage) {
                if (stage === 'settings') {
                    let tabIndex = $scope.tabIndices.settings;
                    if (tabId === 'events') {
                        tabIndex = $scope.tabIndices.event;
                    } else if (tabId === 'data') {
                        tabIndex = $scope.tabIndices.data;
                    }
                    $scope.selectTab($scope.sectionTabs[tabIndex]);
                    angular
                        .element('.detector-wizard-data-table')
                        .removeClass('show-off-detector-wizard-screen')
                        .detach()
                        .appendTo(angular.element('.settings-legend-data-table-parent'));
                    angular
                        .element('.detector-wizard-events-table')
                        .removeClass('show-off-detector-wizard-screen')
                        .detach()
                        .appendTo(angular.element('.settings-legend-events-parent'));
                    checkForMoreOptions();
                    maybeFetchSuggestedThreshold();
                } else {
                    ctrl.showSuggestedThreshold = false;
                    resetSuggestionState();
                    cleanupSuggestionStream();
                }
            }

            $scope.selectTab = function (tab) {
                $scope.selectedTab = tab;
                ctrl.onSelectTab({ tab: tab });
            };

            function onPlotTimeSeriesData(ev, plotKeyToInfoMap) {
                if (plotKeyToInfoMap) {
                    Object.entries(plotKeyToInfoMap).forEach(([plotKey, plotInfo]) => {
                        plotRollupByKey[plotKey] = safeLookup(plotInfo, 'commonRollupType.value');
                    });
                }
            }

            function addCompoundCondition() {
                ctrl.rule.conditions.push({
                    thresholdMode: 'above',
                    triggerMode: 'percent of duration',
                    percentOfDuration: 80,
                    duration: '5m',
                    targetPlot: ctrl.rule.targetPlot,
                    operator: defaultOperator,
                });
            }

            function removeCondition(idx) {
                ctrl.rule.conditions.splice(idx, 1);
            }

            function moveCondition(from, to) {
                const lengthOfConditions = ctrl.rule.conditions.length;
                if (from < 0) {
                    from = 0;
                }

                if (to < 0) {
                    to = 0;
                }

                if (from >= lengthOfConditions) {
                    from = lengthOfConditions - 1;
                }

                if (to >= lengthOfConditions) {
                    to = lengthOfConditions - 1;
                }
                const itemToMove = ctrl.rule.conditions.splice(from, 1)[0];
                // reset the operator
                if (to === 0) {
                    ctrl.rule.conditions[0].operator = itemToMove.operator;
                    delete itemToMove.operator;
                }

                // if moving from first one condition, we need to set the operator
                if (from === 0 && to === lengthOfConditions - 1) {
                    itemToMove.operator = defaultOperator;
                    delete ctrl.rule.conditions[0].operator;
                }
                ctrl.rule.conditions.splice(to, 0, itemToMove);
            }

            $scope.$watch(
                '$ctrl.rule.conditions',
                () => {
                    let orSelected;
                    let andSelected;
                    if (ctrl.rule.conditions) {
                        const targetPlots = [];
                        ctrl.rule.conditions.forEach((condition) => {
                            if (condition.operator === orOperator) {
                                orSelected = true;
                            }

                            if (condition.operator === andOperator) {
                                andSelected = true;
                            }
                            targetPlots.push(condition.targetPlot);
                        });

                        if (_.uniq(targetPlots).length !== 1) {
                            $scope.$emit('toggle compound conditions', true);
                        }
                    }

                    ctrl.bothOperatorSelected = orSelected && andSelected;
                },
                true
            );

            function maybeFetchSuggestedThreshold() {
                if (!ctrl.isSuggestedThresholdEnabled) {
                    ctrl.showSuggestedThreshold = false;
                    return;
                }
                const isStaticThreshold =
                    safeLookup(ctrl, 'currentSelection.function.type') === 'static';

                const targetPlot = getTargetPlot();

                if (!isStaticThreshold || !targetPlot || isDisallowedRollup(targetPlot)) {
                    ctrl.showSuggestedThreshold = false;
                    resetSuggestionState();
                    cleanupSuggestionStream();
                    return;
                }

                ctrl.showSuggestedThreshold = true;
                ctrl.suggestedThresholdState = { status: 'loading' };
                const programText = buildProgramTextForTargetPlot(targetPlot);
                startSuggestionStream(programText);
            }

            function startSuggestionStream(programText) {
                cleanupSuggestionStream();
                suggestionTsidToMetadata = {};

                if (!programText) {
                    handleSuggestionError('Unable to build program text');
                    return;
                }
                const now = Date.now();
                const streamOptions = {
                    signalFlowText: programText,
                    startTimeAbsolute: now - 2 * DEFAULT_RESOLUTION_PERIOD,
                    stopTimeAbsolute: now,
                    resolution: DEFAULT_RESOLUTION_PERIOD,
                    metaDataUpdated: function (metadata, tsid) {
                        suggestionTsidToMetadata[tsid] = metadata;
                    },
                    callback: function (dataPoint) {
                        const metadata = suggestionTsidToMetadata[dataPoint.tsid];
                        if (metadata.sf_streamLabel === 'HIGHER') {
                            latestHigherValue = dataPoint.value;
                        } else if (metadata.sf_streamLabel === 'LOWER') {
                            latestLowerValue = dataPoint.value;
                        }
                        finalizeSuggestion();
                    },
                    streamStopCallback: finalizeSuggestion,
                    onStreamError: function () {
                        handleSuggestionError('Unable to fetch suggested threshold');
                    },
                };

                suggestionStreamHandle = signalStream.stream(streamOptions);
                if (!suggestionStreamHandle) {
                    handleSuggestionError('Unable to start suggestion stream');
                    return;
                }

                suggestionTimeoutPromise = $timeout(
                    () => handleSuggestionError('Fetching the suggested threshold timed out'),
                    DEFAULT_TIMEOUT
                );
            }

            function finalizeSuggestion() {
                if (ctrl.suggestedThresholdState.status !== 'loading') {
                    return;
                }

                const hasHigher = latestHigherValue !== null && latestHigherValue !== undefined;
                const hasLower = latestLowerValue !== null && latestLowerValue !== undefined;

                if (!hasHigher || !hasLower) {
                    return;
                }

                cleanupSuggestionStream();
                ctrl.suggestedThresholdState = {
                    status: 'ready',
                    higherValue: latestHigherValue,
                    lowerValue: latestLowerValue,
                };
            }

            function handleSuggestionError(message) {
                cleanupSuggestionStream();
                ctrl.suggestedThresholdState = {
                    status: 'error',
                    error: message,
                };
            }

            function resetSuggestionState() {
                latestHigherValue = null;
                latestLowerValue = null;
                ctrl.suggestedThresholdState = { status: 'loading' };
            }

            function cleanupSuggestionStream() {
                if (suggestionStreamHandle && suggestionStreamHandle.stopStream) {
                    suggestionStreamHandle.stopStream();
                }
                suggestionStreamHandle = null;
                if (suggestionTimeoutPromise) {
                    $timeout.cancel(suggestionTimeoutPromise);
                    suggestionTimeoutPromise = null;
                }
            }

            function getTargetPlot() {
                const targetKey = chartbuilderUtil.getUniqueKeyFromLetter(
                    safeLookup(ctrl, 'rule.targetPlot')
                );
                return (ctrl.plots || []).find((plot) => plot.uniqueKey === targetKey);
            }

            function isDisallowedRollup(targetPlot) {
                const rollupPolicy = safeLookup(targetPlot, 'configuration.rollupPolicy');
                const targetRollup = rollupPolicy || plotRollupByKey[targetPlot.uniqueKey];
                return (
                    targetRollup === 'SUM_ROLLUP' ||
                    targetRollup === 'COUNT_ROLLUP' ||
                    targetRollup === 'SUM' ||
                    targetRollup === 'COUNT'
                );
            }

            function buildProgramTextForTargetPlot(targetPlot) {
                const targetPlotKey = safeLookup(ctrl, 'rule.targetPlot');
                const allPlots =
                    plotUtils.getAllRelevantPlotAndRatioObjects(targetPlotKey, ctrl.plots || []) ||
                    [];
                if (!allPlots.length && targetPlot) {
                    allPlots.push(angular.copy(targetPlot));
                }
                allPlots.forEach((plot) => {
                    plot.invisible = true;
                });
                const model = { allPlots };
                const programText = programTextUtils.getV2ProgramText(model, true);
                return (
                    "def predict_static_thresholds_median_mad(stream, window=duration('14d'), fluctuation_multiplier=13):\n" +
                    '    _duration = duration(window)\n' +
                    '    median = stream.percentile(pct=50, over=_duration)\n' +
                    '\n' +
                    '    # Calculate MAD: median of absolute deviations from the median\n' +
                    '    mad = abs(stream-median).median(over=_duration)\n' +
                    '\n' +
                    '    # Calculate thresholds\n' +
                    '    upper_threshold = (median + (fluctuation_multiplier * mad)).mean()\n' +
                    '    lower_threshold = (median - (fluctuation_multiplier * mad)).mean().map(lambda x: x if x > 0 else 0)\n' +
                    '\n' +
                    '    return (lower_threshold, upper_threshold)\n' +
                    '\n' +
                    programText +
                    '\n' +
                    'LOWER, HIGHER = predict_static_thresholds_median_mad(' +
                    targetPlotKey +
                    ')\n' +
                    "HIGHER.publish(label='HIGHER')" +
                    '\n' +
                    "LOWER.publish(label='LOWER')"
                );
            }
        },
    ],
});
